import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import {
  AuthenticationService,
  AuthSessionResponse,
  TopconApp,
  useTranslation,
} from '@topcon/topcon-utils';
import { QueryClientProvider } from '@tanstack/react-query';
import type { MetricType } from 'web-vitals';
import type { TFunction } from 'i18next';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/400-italic.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto-mono/500.css';

import { queryClient } from './api/queryClient';
import { setupApiClients } from './api/clients/setup';
import './index.css';
import { initI18n } from './i18n/i18next';
import reportWebVitals from './reportWebVitals';
import { useRoutes } from './app/routes/main.routes';
import { AppProvider, DEFAULT_APP_CONFIG, useAppService } from './providers/app/app.context';
import { FilterProvider } from './providers/filter/filter.context';
import { ErrorDrawerProvider } from './providers/ErrorDrawerContext/error-drawer.context';
import { GeolocationProvider } from './hooks/useGeolocation';

function useAuthCallback(_profile: AuthSessionResponse | null, authService: AuthenticationService) {
  if (!_profile) {
    return true;
  }

  // Temporary grant for "shared" links
  if (!_profile.identityId) {
    if (!window.location.pathname.startsWith('/share')) {
      window.location.replace(authService.getLoginURL());
    }
    return true;
  }

  return true;
}

function LanguageSetter({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();

  // set language based on browser language
  useEffect(() => {
    const browserLanguages = window.navigator.languages?.length
      ? window.navigator.languages
      : [window.navigator.language];

    const normalizedLanguage = browserLanguages
      .map((language) => {
        if (i18n.options.resources && Object.keys(i18n.options.resources).includes(language)) {
          return language;
        }

        const languageCode = language.split('-')[0];
        if (languageCode === 'en') return 'en-US';

        return i18n.options.resources && Object.keys(i18n.options.resources).includes(languageCode)
          ? languageCode
          : undefined;
      })
      .find((language): language is string => Boolean(language));

    // final check to ensure the language is supported
    // requires fallbackLng to be set in i18n init
    const userLang = normalizedLanguage ?? (i18n.options.fallbackLng as string);

    // prevent unnecessary language change calls
    if (userLang !== i18n.language) i18n.changeLanguage(userLang);
  }, [i18n]);
  return <>{children}</>;
}

function ProfileConfigChanger({ t, children }: { t: TFunction; children: React.ReactNode }) {
  const { setConfig, currentBusinessPartner, loadedBusinessPartner, loadedIdentity } =
    useAppService();

  useEffect(() => {
    if (currentBusinessPartner || !loadedBusinessPartner || !loadedIdentity) {
      setConfig(DEFAULT_APP_CONFIG);
    } else {
      // No current business partner - set profile app name/title
      setConfig({ appName: t('profile.appName'), title: t('profile.title') });
    }
  }, [setConfig, currentBusinessPartner, loadedBusinessPartner, loadedIdentity, t]);

  return <>{children}</>;
}

// Component that uses the dynamic routes
function AppWithRoutes() {
  const routes = useRoutes();
  const { config } = useAppService();
  const { t } = useTranslation();

  return (
    <TopconApp
      routes={routes}
      appName={config.appName}
      env={import.meta.env.MODE === 'local-dev' ? 'local' : undefined}
      retainSwitchURL={false}
      useAuthCallback={useAuthCallback}
      notificationDelay={{
        info: 5000,
        success: 5000,
        warning: null,
        error: null,
        neutral: 5000,
        default: 5000,
      }}
      appProviders={(children) => (
        <LanguageSetter>
          <ProfileConfigChanger t={t}>
            <ErrorDrawerProvider>
              <GeolocationProvider>{children}</GeolocationProvider>
            </ErrorDrawerProvider>
          </ProfileConfigChanger>
        </LanguageSetter>
      )}
      hideMyTopconLink
      relativeMyTopcon
    />
  );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// Register etag / If-Match / error-status interceptors on the generated clients.
setupApiClients();

initI18n()
  .then(() => {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <AppProvider>
            <FilterProvider>
              <AppWithRoutes />
            </FilterProvider>
          </AppProvider>
        </QueryClientProvider>
      </React.StrictMode>
    );
  })
  .catch((error: unknown) => {
    console.error('[i18n] Failed to initialise translations:', error);
    root.render(
      <React.StrictMode>
        <p style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
          Failed to load the application. Please refresh the page.
        </p>
      </React.StrictMode>
    );
  });

function reportWebVitalsCallback(metric: MetricType) {
  if (metric.rating !== 'good') {
    console.warn('Web Vitals Needs Attention', metric);
  }
}

if (import.meta.env.MODE !== 'production') {
  reportWebVitals(reportWebVitalsCallback);
}
